import { createContext, useState, ReactNode, useEffect, useRef } from 'react';
import useApi from 'hooks/useApi';
import { takeImpression } from '@rakcode/shimon';
import { useGeolocated } from 'react-geolocated';
import axiosServices from 'utils/axios';
import { format,addDays } from 'date-fns';
type BrowserDetails = {
  useragent: string;
  language: string;
  platform: string;
  timezone: string;
  fingerprint: string;
};

type SelectionContextType = {
  selectedClient: string | null;
  setSelectedClient: (client: string | null) => void;
  selectedTeam: string | null;
  setSelectedTeam: (team: string | null) => void;
  teamDetails: any;
  browserDetails: BrowserDetails | null;
  fingerprint: any;
  setFingerprint: (fp: any) => void;
  publicIp: string | null;
  setPublicIp: (ip: string | null) => void;
  location: any;
  setLocation: (loc: any) => void;
  captureLocationWithAddress: () => Promise<any>;
  clientOptions: any[];
  isLoadingClients: boolean;
  refreshClients: () => Promise<void>;
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;
};

type DateRange = {
  startDate: string; // dd/MM/yyyy format
  endDate: string;   // dd/MM/yyyy format
};

const SelectionContext = createContext<SelectionContextType | undefined>(undefined);

const LOCATION_MAX_AGE_MS = 10 * 60 * 1000;

export const SelectionProvider = ({ children }: { children: ReactNode }) => {
  const { getTeamDetailsByUid, getClientsList, user } = useApi();
  const [selectedClient, setSelectedClientState] = useState<string | null>(() => {
    return localStorage.getItem("selectedClient");
  });
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null);
  const [teamDetails, setTeamDetails] = useState<any>(null);
  const [browserDetails, setBrowserDetails] = useState<BrowserDetails | null>(null);
  const [fingerprint, setFingerprint] = useState<any>(null);
  const [publicIp, setPublicIp] = useState<string | null>(null);
  const [location, setLocation] = useState<any>(null);
  const [clientOptions, setClientOptions] = useState<any[]>([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const today = new Date();
    const startDate = addDays(today, -20); // 20 days before today
    const endDate = addDays(today, 20);    // 20 days after today
    return {
      startDate: format(startDate, 'dd/MM/yyyy'),
      endDate: format(endDate, 'dd/MM/yyyy')
    };
  });
  // Load clients function
  const loadClients = async () => {
    if (isLoadingClients) return; // Prevent duplicate calls
    
    console.log('Starting to load clients...');
    setIsLoadingClients(true);
    try {
      const response = await getClientsList();
      console.log('Client API response:', response);
      if (response?.success && response?.data) {
        console.log('Setting client options:', response.data);
        setClientOptions(response.data);

        // Auto-select client if there's more than one option and no client is currently selected
        if (response.data.length === 1) {
          setSelectedClient(response.data[0].uid);
          console.log('Auto-selected client from context:', response.data[0].name);
        } else {
          console.log('Multiple clients available, please select one.');
        }
      } else {
        console.log('Failed to get client data or no data available');
        setClientOptions([]);
      }
    } catch (error) {
      console.error('Failed to load clients in context:', error);
      setClientOptions([]);
    } finally {
      setIsLoadingClients(false);
      console.log('Finished loading clients');
    }
  };
  const setSelectedClient = (client: string | null) => {
    if (client) {
      localStorage.setItem("selectedClient", client);
    } else {
      localStorage.removeItem("selectedClient");
    }
    setSelectedClientState(client);
  };
  // Refresh clients function for manual refresh
  const refreshClients = async () => {
    await loadClients();
  };

  // Load clients on mount, but only after user is authenticated
  useEffect(() => {
    const userId = user?.id || user?.uid;
    if (userId) {
      console.log('User authenticated, loading clients...');
      loadClients();
    }
  }, [user?.id, user?.uid]); // Only depend on user ID, not full user object

  const { coords } = useGeolocated({
    positionOptions: {
      enableHighAccuracy: true
    },
    userDecisionTimeout: 5000
  });
  
  // Reverse-geocode through the backend, which answers from a shared cache and
  // only calls Google on a miss. The dashboard's Maps key is only for drawing maps.
  const getLocationTitle = async (lat: number, lng: number): Promise<string> => {
    try {
      const res = await axiosServices.get('geo/reverse', { params: { lat, lng } });
      return res.data?.data?.address || 'Unknown location';
    } catch (err) {
      console.error('❌ Reverse geocode failed:', err);
      return 'Unknown location';
    }
  };

  // Dashboard submits come from a desk, so the location barely moves between
  // them. The last geocoded location is reused instead of waiting on a fresh
  // browser lookup before every submit (that wait took up to 8 s), and is
  // refreshed in the background once it is older than LOCATION_MAX_AGE_MS.
  const lastLocationRef = useRef<{ loc: any; at: number } | null>(null);
  const refreshingRef = useRef<Promise<any> | null>(null);

  const geocodeAndRemember = async ({ latitude, longitude, accuracy }: { latitude: number; longitude: number; accuracy: number }) => {
    const title = await getLocationTitle(latitude, longitude);
    const loc = { coordinates: [latitude, longitude], accuracy, location: title };
    lastLocationRef.current = { loc, at: Date.now() };
    setLocation(loc);
    return loc;
  };

  const refreshLocation = (): Promise<any> => {
    if (!refreshingRef.current) {
      refreshingRef.current = new Promise<GeolocationPosition>((resolve, reject) =>
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          // A desktop has no GPS — high accuracy only made the lookup slower.
          enableHighAccuracy: false,
          maximumAge: LOCATION_MAX_AGE_MS,
          timeout: 2000
        })
      )
        .then((pos) => geocodeAndRemember(pos.coords))
        .catch((err) => {
          console.error('Location lookup failed:', err);
          return null;
        })
        .finally(() => {
          refreshingRef.current = null;
        });
    }
    return refreshingRef.current;
  };

  // Called at step submission. Only the first submit of a session waits, and
  // then only on the reverse-geocode when the page-load fix is already in hand.
  const captureLocationWithAddress = async (): Promise<any> => {
    const last = lastLocationRef.current;
    if (last) {
      if (Date.now() - last.at > LOCATION_MAX_AGE_MS) void refreshLocation();
      return last.loc;
    }
    if (coords) return geocodeAndRemember(coords);
    return refreshLocation();
  };

  const fetchPublicIp = async () => {
    try {
      const response = await fetch('https://api64.ipify.org?format=json');
      const data = await response.json();
      console.log('🌐 Public IP:', data.ip);
      setPublicIp(data.ip);
    } catch (err) {
      console.error('Failed to fetch public IP:', err);
      setPublicIp('Unavailable');
    }
  };

  const getFingerprint = async () => {
    const result = await takeImpression();
    console.log('📌 Fingerprint result:', result.fingerprint);
    setFingerprint(result);
  };
  useEffect(() => {
    getFingerprint();
    fetchPublicIp();
  }, []);

  useEffect(() => {
    const detectBrowserDetails = async () => {
      const details: BrowserDetails = {
        useragent: navigator.userAgent,
        language: navigator.language || 'unknown',
        platform: navigator.platform || 'unknown',
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'unknown',
        fingerprint: fingerprint
      };
      setBrowserDetails(details);
    };

    detectBrowserDetails();
  }, []);

  useEffect(() => {
    const fetchTeamDetails = async () => {
      if (selectedTeam) {
        try {
          const details = await getTeamDetailsByUid(selectedTeam);
          setTeamDetails(details);
          console.log(details, 'DetailsContext');
        } catch (err) {
          console.error('Failed to fetch team details:', err);
          setTeamDetails(null);
        }
      } else {
        setTeamDetails(null);
      }
    };
    fetchTeamDetails();
  }, [selectedTeam]); // Remove getTeamDetailsByUid dependency that changes on token refresh

  return (
    <SelectionContext.Provider
      value={{
        selectedClient,
        setSelectedClient,
        selectedTeam,
        setSelectedTeam,
        teamDetails,
        browserDetails,
        fingerprint,
        setFingerprint,
        publicIp,
        setPublicIp,
        location,
        setLocation,
        captureLocationWithAddress,
        clientOptions,
        isLoadingClients,
        refreshClients,
        dateRange,
        setDateRange
      }}
    >
      {children}
    </SelectionContext.Provider>
  );
};

export default SelectionContext;
